import { Button, Row, theme } from 'antd';
import { AxiosRequestConfig, AxiosResponse } from 'axios';
import { TextChangeHandler } from 'quill';
import { MouseEvent, ReactNode, useCallback, useEffect, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuill } from 'react-quilljs';
import grispiApi from '../../apis/grispiApi';
import useRenderedCompanySignature from '../../hooks/ticketDetailPage/useRenderedCompanySignature';
import useEventListener from '../../hooks/useEventListener';
import useMutationObserver from '../../hooks/useMutationObserver';
import { getMentionableUsers } from '../../services/people/userServices';
import { UploadFilesResponse } from '../../services/upload/uploadFiles';
import { grispiNotification } from '../../store/useNotificationStore';
import { getSerializedValueFromUser } from '../../utils/forms/formFieldsHelper.values';
import { nullishValue } from '../../utils/helpers';
import uuid from '../../utils/uuid';
import CustomDivider from '../CustomDivider';
import TranslateAppContainer from '../plugins/translate/TranslateAppContainer';
import UploadController, { GrispiFile, UploadControllerProps, checkFileSizeLimit } from './components/UploadController';
import { EMPTY_EDITOR_VALUE, SIGNATURE_CLASS_NAME } from './constants/richTextEditor';
const CustomUndo = () => (
    <svg viewBox="0 0 18 18">
        <polygon className="ql-fill ql-stroke" points="6 10 4 12 2 10 6 10" />
        <path className="ql-stroke" d="M8.09,13.91A4.6,4.6,0,0,0,9,14,5,5,0,1,0,4,9" />
    </svg>
);

const CustomRedo = () => (
    <svg viewBox="0 0 18 18">
        <polygon className="ql-fill ql-stroke" points="12 10 14 12 16 10 12 10" />
        <path className="ql-stroke" d="M9.91,13.91A4.6,4.6,0,0,1,9,14a5,5,0,1,1,5-5" />
    </svg>
);
const CustomDividerSVG = () => (
    <svg className="ql-fill ql-stroke" viewBox="0 0 18 18">
        <path className="ql-stroke" d="M2 8a.5.5 0 0 1 .5-.5h11a.5.5 0 0 1 0 1h-11A.5.5 0 0 1 2 8Z" />
    </svg>
);

const CustomSignatureIcon = () => (
    <svg width="17" height="17" viewBox="0 0 28 28" xmlns="http://www.w3.org/2000/svg">
        <path
            className="ql-fill ql-stroke"
            d="M16.4798956,21.0019578 L16.75,21 C17.9702352,21 18.6112441,21.5058032 19.4020627,22.7041662 L19.7958278,23.3124409 C20.1028266,23.766938 20.2944374,23.9573247 20.535784,24.0567929 C20.9684873,24.2351266 21.3271008,24.1474446 22.6440782,23.5133213 L23.0473273,23.3170319 C23.8709982,22.9126711 24.4330286,22.6811606 25.0680983,22.5223931 C25.4699445,22.4219316 25.8771453,22.6662521 25.9776069,23.0680983 C26.0780684,23.4699445 25.8337479,23.8771453 25.4319017,23.9776069 C25.0371606,24.0762922 24.6589465,24.2178819 24.1641364,24.4458997 L23.0054899,25.0032673 C21.4376302,25.7436944 20.9059009,25.8317321 19.964216,25.4436275 C19.3391237,25.1860028 18.9836765,24.813298 18.4635639,24.0180227 L18.2688903,23.7140849 C17.7071112,22.8288731 17.4057794,22.5384538 16.8688444,22.5036655 L16.75,22.5 C16.2017306,22.5 16.0933104,22.5684572 14.009281,24.1150241 C12.2670395,25.4079488 10.9383359,26.0254984 9.24864243,26.0254984 C7.18872869,26.0254984 5.24773367,25.647067 3.43145875,24.8905363 L6.31377803,24.2241784 C7.25769404,24.4250762 8.23567143,24.5254984 9.24864243,24.5254984 C10.5393035,24.5254984 11.609129,24.0282691 13.1153796,22.9104743 L14.275444,22.0545488 C15.5468065,21.1304903 15.8296113,21.016032 16.4798956,21.0019578 L16.4798956,21.0019578 Z M22.7770988,3.22208979 C24.4507223,4.8957133 24.4507566,7.60916079 22.7771889,9.28281324 L21.741655,10.3184475 C22.8936263,11.7199657 22.8521526,13.2053774 21.7811031,14.279556 L18.7800727,17.2805874 L18.7800727,17.2805874 C18.4870374,17.5733384 18.0121637,17.573108 17.7194126,17.2800727 C17.4266616,16.9870374 17.426892,16.5121637 17.7199273,16.2194126 L20.7188969,13.220444 C21.2039571,12.7339668 21.2600021,12.1299983 20.678941,11.3818945 L10.0845437,21.9761011 C9.78635459,22.2743053 9.41036117,22.482705 8.99944703,22.5775313 L2.91864463,23.9807934 C2.37859061,24.1054212 1.89457875,23.6214094 2.0192066,23.0813554 L3.42247794,17.0005129 C3.51729557,16.5896365 3.72566589,16.2136736 4.0238276,15.9154968 L16.7165019,3.22217992 C18.3900415,1.54855555 21.1034349,1.54851059 22.7770988,3.22208979 Z M17.7771889,4.28281324 L5.08451462,16.9761302 C4.98512738,17.0755224 4.91567061,17.2008434 4.88406473,17.3378022 L3.7506248,22.2493752 L8.66215777,21.1159445 C8.79912915,21.0843357 8.92446029,21.0148692 9.02385665,20.9154678 L21.7165019,8.22217992 C22.8043063,7.13432049 22.8042862,5.37060478 21.7164588,4.2827701 C20.6285914,3.19496507 18.8649506,3.19499653 17.7771889,4.28281324 Z"
        ></path>
    </svg>
);
function undoChange() {
    this.quill.history.undo();
}
function redoChange() {
    this.quill.history.redo();
}

const formats = [
    'header',

    // 'font', // no-usage
    // 'size', // no-usage

    'bold',
    'italic',
    'underline',
    'strike',
    'link',

    'list',
    'bullet',
    'indent',

    'blockquote',

    // 'align', // gives error currently
    'color',
    'background',

    // 'script', // no-usage
    'image',
    'code-block',
    // 'code', // no-usage

    // to make plugins come alive
    'mention',
    'imageBlot',
    'divider',
    'emoji',
    'privateClass',
    'signatureClass',
    'linkWithGrispiAttribute',
    // to not lose img tag attributes
    'width',
    'height',
    'style'
];

interface QuillGrispiRichTextEditorProps {
    chatInput?: boolean;
    chatInputSendButton?: ReactNode;
    chatInputUploadButton?: ReactNode;
    error?: boolean;
    eventListenerKey?: string;
    internalView?: boolean;
    onChange?: (value: string) => void;
    readOnly?: boolean;
    showTranslate?: boolean;
    simpleEditor?: boolean;
    showSignature?: boolean;
    uploadProps?: UploadControllerProps;
    value: string;
    hideHtmlManipulationsOnToolbar?: boolean;
}

const QuillGrispiRichTextEditor = ({
    chatInput = false,
    chatInputSendButton,
    chatInputUploadButton,
    error = false,
    eventListenerKey,
    internalView = false,
    onChange,
    readOnly = false,
    showTranslate = false,
    simpleEditor = false,
    showSignature = false,
    hideHtmlManipulationsOnToolbar = false,
    value,
    uploadProps = {} as UploadControllerProps
}: QuillGrispiRichTextEditorProps) => {
    const {
        token: { borderRadius, colorBgContainer, colorErrorHover, colorBorder, lineWidth, gold1, paddingXS }
    } = theme.useToken();
    const { t } = useTranslation('common');

    const rootDivRef = useRef<HTMLDivElement>(null);
    const initialValueHydrated = useRef(false);
    const { data: signature, isLoading: isLoadingSignature } = useRenderedCompanySignature(showSignature);
    const { onUploadSuccess, addAttachment } = uploadProps;
    const toolbarId = useMemo(() => uuid(), []);

    const uploader = useMemo(
        () => ({
            upload: async (file: File) => {
                checkFileSizeLimit(file, true);

                const fmData = new FormData();
                fmData.append('files', file);

                const config: AxiosRequestConfig = { headers: { 'content-type': 'multipart/form-data' } };
                try {
                    const res: AxiosResponse<UploadFilesResponse[]> = await grispiApi.post(
                        '/attachments/upload?inline=true',
                        fmData,
                        config
                    );
                    onUploadSuccess(res.data[0] as unknown as GrispiFile, true);

                    addAttachment(res.data[0] as unknown as GrispiFile);
                    return { id: res.data[0].id, url: res.data[0].objectUrl };
                } catch (error) {
                    grispiNotification('error', 'File upload failed, try again', 'file-upload-error');
                }
            }
        }),
        [addAttachment, onUploadSuccess]
    );

    const modules = useMemo(
        () => ({
            // https://github.com/visualjerk/quill-magic-url/issues/59#issuecomment-886722265
            magicUrl: {
                globalRegularExpression: /\b(([\w-]+:\/\/?|www[.])[^\s()<>]+(?:\([\w\d]+\)|([^[:punct:]\s]|\/?)))/gi, // thanks to https://mathiasbynens.be/demo/url-regex @gruber
                urlRegularExpression: /\b(([\w-]+:\/\/?|www[.])[^\s()<>]+(?:\([\w\d]+\)|([^[:punct:]\s]|\/?)))/gi // thanks to https://mathiasbynens.be/demo/url-regex @gruber
            },
            ...(readOnly || simpleEditor || chatInput ? {} : { blotFormatter: {} }),
            ...(simpleEditor || chatInput
                ? {}
                : {
                      mention: {
                          allowedChars: /^[A-Za-z\sÅÄÖåäö]*$/,
                          mentionDenotationChars: ['@'],
                          source: async function (searchTerm, renderList) {
                              if (searchTerm?.length < 3) return;
                              const matchedPeople = await getMentionableUsers(searchTerm);
                              const options = matchedPeople?.result?.map((user) => ({
                                  id: `${getSerializedValueFromUser(user)}|${user.fullName} (${user.email})|mention`,
                                  label: `${user.fullName} (${user.email})`,
                                  value: user.fullName
                              }));
                              renderList(options);
                          },
                          renderItem: (item) => item.label,
                          renderLoading: () => t('searchTermTooShort')
                      }
                  }),
            ...(readOnly || simpleEditor || chatInput ? {} : { uploader }),
            toolbar: readOnly
                ? false
                : {
                      //   container: [
                      //       [{ header: [2, 3, 4, false] }],
                      //       ['bold', 'italic', 'underline', 'strike', 'blockquote'],
                      //       [{ color: [] }, { background: [] }],
                      //       [{ align: [] }, { list: 'ordered' }, { list: 'bullet' }, { indent: '-1' }, { indent: '+1' }],
                      //       [
                      //           //   'link',
                      //           ...(simpleEditor ? [] : ['image']),
                      //           'code'
                      //           //'code-block'],
                      //       ],
                      //       ['clean']
                      //   ]
                      container: '#toolbar' + toolbarId,
                      handlers: {
                          undo: undoChange,
                          redo: redoChange,
                          emoji: function () {}
                      }
                  },
            'emoji-toolbar': true,
            'emoji-shortname': true
        }),
        [chatInput, readOnly, simpleEditor, t, toolbarId, uploader]
    );

    const { quill, quillRef, Quill } = useQuill({
        modules,
        formats: chatInput ? formats.filter((f) => f !== 'imageBlot').filter((f) => f !== 'image') : formats,
        readOnly
    });

    if (Quill && !quill) {
        const SnowTheme = Quill.import('themes/snow');

        class SnowThemeFix extends SnowTheme {
            extendToolbar(toolbar) {
                super.extendToolbar(toolbar);
                this.tooltip.textbox.dataset.link = 'https://grispi.com';
            }
        }

        Quill.register('themes/snow', SnowThemeFix, true);
        // For execute this line only once.
        require('quill-mention');
        const { default: LinkWithGrispiAttribute } = require('./blots/linkWithGrispiAttribute');
        Quill.register('formats/linkWithGrispiAttribute', LinkWithGrispiAttribute);
        const { default: MagicUrl } = require('quill-magic-url');
        Quill.register('modules/magicUrl', MagicUrl);
        const { default: Emoji } = require('quill-emoji');
        Quill.register('modules/emoji', Emoji);
        if (!chatInput) {
            const { default: LoadingImage } = require('./blots/loadingImage');
            Quill.register('formats/imageBlot', LoadingImage);
        }
        const { default: PrivateClass } = require('./blots/privateClass');
        Quill.register(PrivateClass);
        const { default: SignatureClass } = require('./blots/signatureClass');
        Quill.register(SignatureClass);
        const { default: Divider } = require('./blots/divider');
        Quill.register(Divider);
        const { default: GrispiUploader } = require('./modules/grispiUploader');
        Quill.register('modules/uploader', GrispiUploader);
        if (!chatInput) {
            const { default: BlotFormatter } = require('quill-blot-formatter');
            Quill.register('modules/blotFormatter', BlotFormatter);
        }

        const icons = Quill.import('ui/icons');
        icons[
            'image'
        ] = `<svg xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink" version="1.1" width="17" height="17" viewBox="0 0 256 256" xml:space="preserve"><defs></defs><g class="ql-fill ql-stroke"  transform="translate(245.83968871595334 245.8396887159533) rotate(180) scale(2.63 2.63)" >
                <path d="M 27.252 90.004 c -6.221 0 -12.069 -2.423 -16.468 -6.822 c -4.399 -4.398 -6.821 -10.246 -6.821 -16.467 s 2.423 -12.069 6.821 -16.468 l 45.131 -45.13 c 6.819 -6.818 17.913 -6.817 24.728 0 c 6.817 6.818 6.817 17.911 0 24.728 L 38.569 71.918 c -4.554 4.555 -11.965 4.555 -16.521 0 c -4.555 -4.555 -4.555 -11.966 0 -16.521 L 60.43 17.016 c 1.172 -1.172 3.071 -1.172 4.243 0 c 1.172 1.171 1.172 3.071 0 4.243 L 26.291 59.64 c -2.215 2.215 -2.215 5.82 0 8.036 c 2.214 2.215 5.819 2.214 8.035 0 l 42.073 -42.073 c 4.478 -4.478 4.478 -11.765 0 -16.243 c -4.48 -4.478 -11.765 -4.477 -16.243 0 l -45.13 45.129 c -3.265 3.266 -5.063 7.607 -5.063 12.226 c 0 4.618 1.799 8.96 5.063 12.225 c 3.266 3.265 7.608 5.064 12.225 5.064 c 4.618 0 8.96 -1.799 12.225 -5.064 l 41.439 -41.438 c 1.172 -1.172 3.071 -1.172 4.243 0 c 1.172 1.171 1.172 3.071 0 4.243 L 43.72 83.182 C 39.321 87.581 33.473 90.004 27.252 90.004 z"  transform=" matrix(1 0 0 1 0 0) " stroke-linecap="round" /></g>
            </svg>`;
    }

    useEffect(() => {
        if (quill) {
            if (nullishValue(value) || !initialValueHydrated.current) {
                const pasteValue = nullishValue(value) ? '' : value;
                quill.clipboard.dangerouslyPasteHTML(pasteValue);
                quill.blur();
                initialValueHydrated.current = true;
            }
        }
    }, [quill, value]);

    const handleOnChange = useCallback(
        (currentHTMLText: string) => {
            if (readOnly) return;
            if (currentHTMLText === EMPTY_EDITOR_VALUE) return onChange(null);
            onChange(currentHTMLText);
        },
        [readOnly, onChange]
    );
    useEffect(() => {
        function onTextChange(): TextChangeHandler {
            handleOnChange(quill.root?.innerHTML);
            return;
        }

        quill?.on('text-change', onTextChange);
        return () => {
            quill?.off('text-change', onTextChange);
        };
    }, [handleOnChange, quill]);

    const insertTextEventHandler = useCallback(
        (event: WindowEventMap['INSERT_TEXT_TO_RICH_TEXT_EDITOR_STATE_EVENT']) => {
            if (event.detail.eventListenerKey === eventListenerKey) {
                // debugger;
                if (quill) {
                    quill.focus();
                    const range = quill.getSelection().index;
                    if (eventListenerKey === 'add-placeholder-to-macro') {
                        quill.insertText(range, event.detail.textToInsert);
                        return;
                    }
                    quill.clipboard.dangerouslyPasteHTML(range, event.detail.textToInsert);
                    return;
                }
            }
        },
        [quill, eventListenerKey]
    );

    const replaceTextEventHandler = useCallback(
        (event: WindowEventMap['REPLACE_TEXT_TO_RICH_TEXT_EDITOR_STATE_EVENT']) => {
            if (event.detail.eventListenerKey === eventListenerKey && event.detail.textToReplace) {
                if (quill) {
                    quill.clipboard.dangerouslyPasteHTML(event.detail.textToReplace);
                    return;
                }
            }
        },
        [quill, eventListenerKey]
    );
    const resetStateEventHandler = useCallback(
        (event: WindowEventMap['RESET_RICH_TEXT_EDITOR_STATE_EVENT']) => {
            if (event.detail.eventListenerKey === eventListenerKey) {
                if (quill) {
                    quill.clipboard.dangerouslyPasteHTML('');
                    return;
                }
            }
        },
        [quill, eventListenerKey]
    );

    useEventListener('RESET_RICH_TEXT_EDITOR_STATE_EVENT', resetStateEventHandler);
    useEventListener('INSERT_TEXT_TO_RICH_TEXT_EDITOR_STATE_EVENT', insertTextEventHandler);
    useEventListener('REPLACE_TEXT_TO_RICH_TEXT_EDITOR_STATE_EVENT', replaceTextEventHandler);

    const mutationRef = useRef<Element>();
    useEffect(() => {
        const linkEditingElement = document.querySelector(`#rich-text-comment-${toolbarId} .ql-tooltip`);

        if (linkEditingElement) mutationRef.current = linkEditingElement;
    });
    useMutationObserver(
        mutationRef,
        (mutations) => {
            let styleFixed = false;
            mutations.forEach((mutation) => {
                const { target } = mutation;
                const targetElement: HTMLElement = target as HTMLElement;

                const isVisible = !styleFixed && !targetElement?.classList?.contains('ql-hidden');
                const isStyleFixRequired = isVisible && !styleFixed;

                if (isStyleFixRequired) {
                    const rectBoundary: DOMRect = rootDivRef.current?.getBoundingClientRect();
                    const rect: DOMRect = targetElement?.getBoundingClientRect();

                    if (rectBoundary.left > rect.left) {
                        targetElement?.style?.setProperty('left', 5 + 'px');
                        styleFixed = true;
                    }
                    if (rect.right > rectBoundary.right) {
                        targetElement?.style?.setProperty('left', rectBoundary.width - rect.width - 5 + 'px');
                        styleFixed = true;
                    }
                }
            });
        },
        { attributes: true, attributeOldValue: true, attributeFilter: ['class'] }
    );

    const handleSignature = (e: MouseEvent<HTMLButtonElement>) => {
        //behavior of signature button is: if signature not exists,will add signature. if signature exists,will remove signature
        e.preventDefault();
        e.stopPropagation();
        if (quill) {
            const focusRange = quill.getSelection(true); //get range for cursor location
            const signatureElement = document.querySelectorAll(
                `#quill-editor-${toolbarId} .ql-editor .${SIGNATURE_CLASS_NAME}`
            ); //select signatures
            if (signatureElement.length > 0)
                //if signature exists, remove signature
                signatureElement.forEach((element) => element.remove());
            else {
                //if signature not exists, add signature
                const range = quill.getLength(); //range for end of editor
                quill.clipboard.dangerouslyPasteHTML(range, `<div class='${SIGNATURE_CLASS_NAME}'>${signature}</div>`); //wrapper for signature
                quill.setSelection(focusRange); // set range to cursor location after add signature
            }
            return;
        }
    };

    const insertDivider = () => {
        // e.stopPropagation();
        // e.preventDefault();
        const range = quill.getSelection(true);
        quill.insertText(range.index, '\n', Quill.sources.USER);
        quill.insertEmbed(range.index + 1, 'divider', true, Quill.sources.USER);
        quill.setSelection(range.index + 2, Quill.sources.SILENT);
    };

    return (
        <div
            ref={rootDivRef}
            style={{
                border: `${lineWidth}px solid ${error ? colorErrorHover : colorBorder}`,
                borderRadius,
                ...(chatInput ? { width: '100%' } : {})
            }}
        >
            <div
                id={`rich-text-comment-${toolbarId}`}
                style={{
                    backgroundColor: !internalView ? colorBgContainer : gold1,
                    borderRadius
                }}
            >
                {!readOnly && (
                    <div
                        id={`toolbar${toolbarId}`}
                        className="ql-toolbar ql-snow"
                        style={{ border: 'none', borderRadius }}
                    >
                        <span className="ql-formats">
                            <button className="ql-undo">
                                <CustomUndo />
                            </button>
                            <button className="ql-redo">
                                <CustomRedo />
                            </button>
                        </span>
                        {!hideHtmlManipulationsOnToolbar && (
                            <>
                                {!chatInput && (
                                    <span className="ql-formats">
                                        <select className="ql-header" defaultValue="5">
                                            <option value="2">Heading 1</option>
                                            <option value="3">Heading 2</option>
                                            <option value="4">Heading 3</option>
                                            <option value="5">Normal</option>
                                        </select>
                                    </span>
                                )}
                                <span className="ql-formats">
                                    <button className="ql-bold" />
                                    <button className="ql-italic" />
                                    <button className="ql-underline" />
                                    <button className="ql-strike" />
                                    <button className="ql-link" />
                                </span>

                                {!chatInput && (
                                    <span className="ql-formats">
                                        <button className="ql-list" value="ordered" />
                                        <button className="ql-list" value="bullet" />
                                        <button className="ql-indent" value="-1" />
                                        <button className="ql-indent" value="+1" />
                                    </span>
                                )}
                                {!chatInput && (
                                    <span className="ql-formats">
                                        <button className="ql-blockquote" />
                                    </span>
                                )}
                                {!chatInput && (
                                    <span className="ql-formats">
                                        {/* TODO: handle error => TypeError: Cannot read properties of null (reading 'innerHTML') Call Stack IconPicker.selectItem node_modules/quill/dist/quill.js (9766:0) */}
                                        {/* <select className="ql-align" /> */}
                                        <select className="ql-color" />
                                        <select className="ql-background" />
                                    </span>
                                )}
                                {!simpleEditor && !chatInput && (
                                    <span className="ql-formats">
                                        <button className="ql-image" />
                                    </span>
                                )}
                                {showSignature && (
                                    <span className="ql-formats">
                                        <button
                                            className="ql-signature"
                                            disabled={!signature || isLoadingSignature}
                                            onClick={handleSignature}
                                        >
                                            <CustomSignatureIcon />
                                        </button>
                                    </span>
                                )}
                                {!chatInput && (
                                    <span className="ql-formats">
                                        <button className="ql-code-block" />
                                        <button className="ql-clean" />
                                        <Button onClick={insertDivider}>
                                            <CustomDividerSVG />
                                        </Button>
                                    </span>
                                )}
                            </>
                        )}
                        {!chatInput && (
                            <span className="ql-formats">
                                <button className="ql-emoji" />
                            </span>
                        )}
                        {chatInput && (
                            <span className="ql-formats">
                                {!hideHtmlManipulationsOnToolbar && (
                                    <>
                                        <Button onClick={insertDivider}>
                                            <CustomDividerSVG />
                                        </Button>
                                        <button style={{ padding: 0 }}>{chatInputUploadButton}</button>
                                    </>
                                )}
                                <button className="ql-emoji" />
                            </span>
                        )}
                    </div>
                )}
                <CustomDivider zeroMargin />
                {chatInput && chatInputSendButton}
                <div
                    ref={quillRef}
                    id={`quill-editor-${toolbarId}`}
                    {...(chatInput ? { className: 'quill-editor-chat-input' } : {})}
                    style={{
                        // send-icon-button width + text-editor-margin
                        width: chatInput ? 'calc(100% - 30px - 15px)' : '100%',
                        border: 'none',
                        borderRadius,
                        minHeight: readOnly ? 'auto' : chatInput ? 60 : 100,
                        maxHeight: readOnly ? 'auto' : chatInput ? 120 : 250,
                        overflow: 'auto'
                    }}
                />
            </div>
            {!internalView && showTranslate && (
                <Row
                    justify="end"
                    align="middle"
                    style={{ paddingLeft: paddingXS, paddingRight: paddingXS, paddingBottom: paddingXS, paddingTop: 0 }}
                >
                    <TranslateAppContainer eventListenerKey={eventListenerKey} commentText={value} />
                </Row>
            )}
            {!readOnly && !simpleEditor && onUploadSuccess && <UploadController {...uploadProps} />}
        </div>
    );
};

export default QuillGrispiRichTextEditor;
